import { useMemo, useState } from 'react'
import GavelRoundedIcon from '@mui/icons-material/GavelRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded'
import ImageRoundedIcon from '@mui/icons-material/ImageRounded'
import type { HelperAssignedItem } from '../../services/helper/helperAssignedItemsService'
import { Pressable } from '../Common'
import { ProofReviewActions } from '../ChecklistProof/ProofReviewActions'

interface HelperApprovalsStripProps {
  items: HelperAssignedItem[]
  actor: { uid: string; displayName: string } | null
  currentUserId: string | null
}

/**
 * Helper-side approvals strip. Shown in the helper dashboard when the
 * signed-in helper is set as `proofApproverId` on any ASSESSING checklist
 * item (helpers can be approvers for other helpers' work).
 */
export const HelperApprovalsStrip = ({ items, actor, currentUserId }: HelperApprovalsStripProps) => {
  const [open, setOpen] = useState(false)
  const pending = useMemo(() => {
    if (!currentUserId) return []
    return items.filter((item) => (
      item.proofApproverId === currentUserId
        && item.workflowStatus === 'ASSESSING'
        && item.proofReviewStatus === 'PENDING'
    ))
  }, [items, currentUserId])

  if (!actor || pending.length === 0) return null

  return (
    <section
      className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] px-3 py-2.5"
      data-testid="helper-approvals-strip"
    >
      <Pressable
        className="w-full flex items-center gap-2 text-left text-[var(--text-strong)]"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        <GavelRoundedIcon className="text-[var(--warning)]" style={{ fontSize: 18 }} />
        <span className="flex-1 text-sm font-semibold">
          {pending.length} item{pending.length === 1 ? '' : 's'} awaiting your approval
        </span>
        {open
          ? <ExpandLessRoundedIcon style={{ fontSize: 18 }} />
          : <ExpandMoreRoundedIcon style={{ fontSize: 18 }} />}
      </Pressable>

      {open ? (
        <ul className="mt-2 flex flex-col gap-2">
          {pending.map((item) => (
            <li key={item.id} className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-3">
              <div className="flex items-baseline justify-between gap-2">
                <strong className="text-sm text-[var(--text-strong)]">{item.title}</strong>
                <small className="text-xs text-[var(--muted)]">by {item.proofUploadedByName ?? 'helper'}</small>
              </div>
              {item.proofFileUrl ? (
                <a
                  className="mt-1 inline-flex items-center gap-1 text-xs text-[var(--accent-strong)]"
                  href={item.proofFileUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ImageRoundedIcon style={{ fontSize: 14 }} />
                  <span>{item.proofFileName ?? 'View proof'}</span>
                </a>
              ) : null}
              <div className="mt-2">
                <ProofReviewActions item={item} actor={actor} compact />
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}
